import { FC } from "react";
import { Tabs, Tab, Box } from "@mui/material";
import Polygon from "@/assets/svg/Polygon";
import theme from "@/theme";

interface SortTabsProps {
  activeTab: "post" | "likes" | "liked" | "sentiment";
  setActiveTab: (val: "post" | "likes" | "liked" | "sentiment") => void;
  order: "asc" | "desc";
  onSortClick: (variant: "Posts" | "Likes" | "Liked" | "Sentiment") => void;
  view: string;
}

const SortTabs: FC<SortTabsProps> = ({ activeTab, setActiveTab, order, onSortClick, view }) => {
  const handleClick = (tab: "post" | "likes" | "liked" | "sentiment") => {
    if (activeTab === tab) {
      onSortClick(
        tab === "post"
          ? "Posts"
          : tab === "likes"
            ? "Likes"
            : tab === "liked"
              ? "Liked"
              : "Sentiment"
      );
    } else {
      setActiveTab(tab);
    }
  };

  const hideAsc = order === "asc";
  const hideDesc = order === "desc";

  const renderLabel = (label: string, tab: "post" | "likes" | "liked" | "sentiment") => {
    const isSentiment = tab === "sentiment";
    const isActive = activeTab === tab;
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "2px",
          justifyContent: "left",
          color: isActive ? theme.palette.custom.mainBlue : "#7D8690",
          fontWeight: isActive ? 600 : 500,
          fontSize: "14px",
          lineHeight: "20px",
        }}
      >
        {label}
        {isSentiment && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              width: "20px",
              height: "20px",
              padding: "1px",
              opacity: 1,
              cursor: "pointer",
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              sx={{
                marginBottom: "1px",
                display: "flex",
                flexDirection: "column",
                width: "12px",
                height: "18px",
                gap: "2px",
                opacity: 1,
                cursor: "pointer",
              }}
            >
              <Box sx={{ display: "contents" }}>
                <Polygon
                  color={hideAsc && activeTab === tab ? theme.palette.custom.mainBlue : "#7D8690"}
                />
              </Box>
              <Box sx={{ display: "contents" }}>
                <Polygon
                  flipped
                  color={hideDesc && activeTab === tab ? theme.palette.custom.mainBlue : "#7D8690"}
                />
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Tabs
      value={activeTab}
      onChange={(_, value) => setActiveTab(value as any)}
      TabIndicatorProps={{
        sx: {
          backgroundColor: theme.palette.custom.mainBlue,
          height: "2px",
          borderRadius: 2,
          bottom: 0,
        },
      }}
      sx={{
        marginBottom: "10px",
        minHeight: "24px",
        height: "24px",
        "& .MuiTab-root": {
          justifyContent: "center",
          alignItems: "flex-start",
        },

        "& .MuiTabs-flexContainer": {
          gap: "16px",
          justifyContent: "space-between",
        },
        "& .MuiButtonBase-root": {
          minHeight: "24px",
          padding: 0,
          textTransform: "none",
          backgroundColor: "transparent",
          minWidth: "auto",
          width: "auto",
          "&.Mui-selected": {
            backgroundColor: "transparent",
          },
          "&:hover": {
            backgroundColor: "transparent",
          },
          "& .MuiTab-root": {
            minWidth: "auto",
            width: "auto",
          },
        },
      }}
      variant="scrollable"
      scrollButtons={false}
    >
      <Tab
        value="post"
        label={renderLabel("Posts", "post")}
        onClick={() => handleClick("post")}
        disableRipple
        disableFocusRipple
      />
      <Tab
        value="likes"
        label={renderLabel("Likes", "likes")}
        onClick={() => handleClick("likes")}
        disableRipple
        disableFocusRipple
      />
      {view === "follows" && (
        <Tab
          value="liked"
          label={renderLabel("Liked", "liked")}
          onClick={() => handleClick("liked")}
          disableRipple
          disableFocusRipple
        />
      )}

      <Tab
        value="sentiment"
        label={renderLabel("Sentiment", "sentiment")}
        onClick={() => handleClick("sentiment")}
        disableRipple
        disableFocusRipple
      />
    </Tabs>
  );
};

export default SortTabs;
